import { SUBTITLE_FONT_INFO, type HookAccent, type HookDesign } from './constants.js';
import { hookLayout, subtitleGroups, type SubtitlePosition } from './typography.js';

/** One composition for SVG preview and libass; sizes use CSS font pixels. */
export const HOOK_FONT = SUBTITLE_FONT_INFO.tiktoksans;
export const HOOK_SCALE_X = 1;
export const HOOK_ACCENT = '#DFFF54';
/**
 * Кривая «Волны» из макета (Figma 28270:1615), в долях кегля: синусоида с амплитудой
 * WAVE.amplitude и периодом WAVE.period; строка начинается с фазы WAVE.phase — во впадине
 * перед подъёмом к гребню. Все строки идут по одной кривой.
 */
const WAVE = { amplitude: 0.507, period: 11.11, phase: 1.11 };
/** `font: 'pair'` — вторая гарнитура «Пары» (HOOK_PAIR_FONT), иначе шрифт хука. */
/** height — высота чернил строки, descent — сколько из неё ниже базовой линии (если измеритель знает). */
export type HookMeasure = (text: string, size: number, font?: 'pair') => { width: number; height: number; descent?: number };
/** Курсив с засечками для второй строки «Пары» (Figma 28254:11377). */
export const HOOK_PAIR_FONT = { label: 'Playfair Display Bold Italic', family: 'Playfair Display Bold Italic',
  file: 'PlayfairDisplay-BoldItalic.ttf', assScale: 0.70922, hasBold: false };
/**
 * Буква строки, поставленная на кривую: (x, y) — левый верх буквы до поворота, как у строки;
 * поворот на `angle` градусов против часовой стрелки вокруг (ox, oy) — точки на кривой.
 */
export interface HookGlyph { text: string; x: number; y: number; ox: number; oy: number; angle: number }
/** У «Волны» строка рисуется по буквам из `glyphs`; `text` остаётся целой строкой. */
export interface HookLine { text: string; x: number; y: number; size: number; width: number; height: number; accent: boolean; color?: string; glyphs?: HookGlyph[]; font?: 'pair' }
export interface HookShape { path: string; accent: boolean; opacity: number; outlineWidth: number; color?: string; strokeAccent?: boolean }

/** Строка, которую выделяет акцент: у «Хайлайта» первая, у остальных последняя из нескольких. */
function accentRow(design: HookDesign, count: number): number {
  if (design === 'highlight') return 0;
  return count > 1 ? count - 1 : -1;
}

/** В «Паре» строки перед последней набираются чуть мельче (18 и 20 в макете). */
function rowScale(design: HookDesign, index: number, count: number): number {
  return design === 'emphasis' && count > 1 && index < count - 1 ? 0.9 : 1;
}
/** В «Паре» последняя строка — курсив с засечками. */
function rowFont(design: HookDesign, index: number, count: number): 'pair' | undefined {
  return design === 'emphasis' && count > 1 && index === count - 1 ? 'pair' : undefined;
}

export function composeHook(text: string, width: number, height: number, measure: HookMeasure,
  position: SubtitlePosition = 'bottom', subtitlesEnabled = true, design: HookDesign = 'card', accentMode: HookAccent = 'normal',
  hookPosition: number | null = null, boxMode: boolean | 'white' | 'black' = true) {
  // Плашка хука следует плашке субтитров: белая или чёрная, false — без плашки у Линии, Ленты и Рамки.
  // true — прежний вид: тёмная плашка у Линии, Ленты и Рамки. У Хайлайтов подложка — сам стиль:
  // она белая, тёмной становится только при чёрной плашке субтитров.
  const box = boxMode !== false;
  const light = boxMode === 'white' || (boxMode !== 'black' && (design === 'highlight' || design === 'liquid'));
  const surface = light ? '#FFFFFF' : '#18181B';
  const ink = light ? '#18181B' : '#FFFFFF';
  const groups = subtitleGroups(text.trim().split(/\s+/u).filter(Boolean).map(text => ({ text })))
    .map(group => group.map(w => w.text).join(' '));
  const layout = hookLayout(position, subtitlesEnabled, hookPosition);
  const inset = width * 0.002;
  const cardX = width * layout.left + inset;
  const cardWidth = width * (layout.right - layout.left) - 2 * inset;
  const paddingY = width * 0.035;
  const maxHeight = height * layout.bandHeight - 2 * paddingY - 2 * inset;
  const center = height * layout.centerY;
  const lower = center > height / 2;
  const left = cardX + width * 0.095;
  const maxWidth = cardWidth - width * 0.13;
  const nominal = width * 0.095;
  const lineGap = design === 'highlight' ? 0.3 : design === 'liquid' ? 0.2 : 0.12;
  // «Хайлайт» сдвигает каждую следующую плашку вправо; «Жидкий» — каждую вторую.
  const stagger = design === 'highlight' || design === 'liquid' ? 0.74 : 0;
  const shift = (i: number) => design === 'liquid' ? (i % 2) * stagger : i * stagger;
  // Волна поднимает и опускает буквы на амплитуду: место под неё резервируем сверху и снизу.
  const swing = design === 'wave' ? 2 * WAVE.amplitude : 0;
  // Текст строк как на экране: «Капсом» — заглавными только акцентная строка, скобки у последней.
  const shown = (rows: string[]) => rows.map((row, i) => {
    const upper = accentMode === 'caps' && i === accentRow(design, rows.length) ? row.toLocaleUpperCase('ru-RU') : row;
    return design === 'brackets' && i === rows.length - 1 ? `[${upper}]` : upper;
  });
  // Find the largest balanced one/two/three-line composition without orphaned connectives.
  let best: { rows: string[]; size: number; score: number } = { rows: [], size: nominal, score: -Infinity };
  const consider = (source: string[]) => {
    const rows = shown(source);
    const metrics = rows.map((row, i) => {
      const m = measure(row, 1, rowFont(design, i, rows.length)), k = rowScale(design, i, rows.length);
      return { width: m.width * HOOK_SCALE_X * k, height: m.height * k, natural: m.width * HOOK_SCALE_X };
    });
    const span = Math.max(0.01, ...metrics.map((m, i) => m.width + shift(i)));
    const stack = metrics.reduce((sum, m) => sum + m.height, 0) + (rows.length - 1) * lineGap;
    // Разбивку выбираем как без волны, а кегль уменьшаем, чтобы поместился её размах.
    const size = Math.min(nominal, maxWidth / span, maxHeight / Math.max(0.01, stack));
    const fit = Math.min(size, maxHeight / Math.max(0.01, stack + swing));
    // Баланс по ширине в полном кегле: мелкая строка «Пары» не должна забирать лишние слова.
    const widths = metrics.map(m => m.natural);
    const balance = Math.min(...widths) / Math.max(...widths, 0.01);
    // В «Паре» крупная последняя строка — сама фраза-акцент: она не уже мелких строк над ней.
    const stub = design === 'emphasis' && rows.length > 1 && widths.at(-1)! < Math.max(...widths.slice(0, -1));
    const score = size * (0.88 + balance * 0.12) * (stub ? 0.6 : 1) - (rows.length - 1) * width * 0.012;
    if (score > best.score) best = { rows, size: fit, score };
  };
  if (groups.length) consider([groups.join(' ')]);
  for (let a = 1; a < groups.length; a++) {
    consider([groups.slice(0, a).join(' '), groups.slice(a).join(' ')]);
    for (let b = a + 1; b < groups.length; b++) {
      consider([groups.slice(0, a).join(' '), groups.slice(a, b).join(' '), groups.slice(b).join(' ')]);
    }
  }
  const count = best.rows.length;
  const accented = accentRow(design, count);
  const metrics = best.rows.map((row, i) => {
    const size = best.size * rowScale(design, i, count);
    const font = rowFont(design, i, count);
    const m = measure(row, size, font);
    return { size, font, width: m.width * HOOK_SCALE_X, height: m.height, descent: m.descent };
  });
  const blockWidth = Math.max(0, ...metrics.map(m => m.width));
  const amplitude = design === 'wave' ? best.size * WAVE.amplitude : 0;
  const blockHeight = metrics.reduce((sum, m) => sum + m.height, 0) + Math.max(0, count - 1) * best.size * lineGap + 2 * amplitude;
  const top = center - blockHeight / 2;
  let y = top + amplitude;
  const lines: HookLine[] = best.rows.map((text, i) => {
    const m = metrics[i]!;
    const line: HookLine = { text, x: left, y, size: m.size, width: m.width, height: m.height,
      accent: i === accented && !['paper', 'frame', 'highlight'].includes(design),
      // Текст на плашке — контрастный ей; на акцентной плашке «Хайлайта» — тёмный; без плашки — белый.
      color: design === 'highlight' && i === accented ? '#18181B'
        : design === 'highlight' || design === 'liquid' || (box && ['card', 'paper', 'frame'].includes(design)) ? ink : '#FFFFFF',
      ...(m.font ? { font: m.font } : {}) };
    if (design === 'paper') line.x = cardX + width * 0.065;
    if (design === 'frame' || design === 'emphasis' || design === 'classic') line.x = cardX + (cardWidth - m.width) / 2;
    if (design === 'highlight') line.x = cardX + width * 0.025 + shift(i) * best.size;
    // У «Жидкого» подложка шире текста: отступ слева, чтобы она не выходила за безопасную зону.
    if (design === 'liquid') line.x = cardX + width * 0.05 + shift(i) * best.size;
    if (design === 'brackets' && i === count - 1 && count > 1) line.x = left + (blockWidth - m.width) / 2;
    if (design === 'wave') line.glyphs = waveGlyphs(line, measure);
    y += m.height + best.size * lineGap;
    return line;
  });
  const card = { x: cardX, y: top - paddingY, width: cardWidth, height: blockHeight + 2 * paddingY, radius: width * 0.035 };
  const shapes: HookShape[] = [];
  const rect = (x: number, y: number, w: number, h: number, accent = false, color = '#18181B', radius = 0, opacity = 1) => {
    shapes.push({ path: roundedRect(x, y, w, h, radius), accent, color, opacity, outlineWidth: 0 });
  };
  // «Без плашки»: у Линии остаются черта и текст, у Рамки — уголки, у Ленты — контур.
  if (groups.length && design === 'card' && box) {
    shapes.push({ path: roundedRect(card.x, card.y, card.width, card.height, card.radius), accent: false, color: surface,
      opacity: light ? 0.92 : 0.74, outlineWidth: light ? 0 : width * 0.0015 });
  }
  if (groups.length && design === 'card') {
    shapes.push({ path: roundedRect(card.x + width * 0.039, top, width * 0.009, Math.max(blockHeight, width * 0.025), width * 0.0045),
      accent: true, opacity: 1, outlineWidth: 0 });
  }
  if (groups.length && design === 'paper') {
    // Ribbon design; retain the persisted "paper" key for existing projects.
    const cut = width * 0.025;
    const ribbon = (x: number, y: number, w: number, h: number, accent: boolean) => {
      shapes.push({ path: `M ${x + cut} ${y} L ${x + w} ${y} L ${x + w - cut} ${y + h} L ${x} ${y + h} Z`,
        accent, color: surface, opacity: 1, outlineWidth: 0 });
    };
    if (box) {
      ribbon(card.x, card.y, card.width, card.height, true);
      ribbon(card.x + width * 0.018, card.y + width * 0.008,
        card.width - width * 0.018, card.height - width * 0.016, false);
    } else {
      // Только контур: внутренний контур идёт в обратную сторону и вырезает середину (правило nonzero).
      const outer = ribbonPoints(card.x, card.y, card.width, card.height, cut);
      const inner = ribbonPoints(card.x + width * 0.018, card.y + width * 0.008, card.width - width * 0.018, card.height - width * 0.016, cut).reverse();
      shapes.push({ path: `${polygon(outer)} ${polygon(inner)}`, accent: true, color: '#18181B', opacity: 1, outlineWidth: 0 });
    }
  }
  if (design === 'highlight') {
    // Отдельная плашка под каждой строкой: первая цветом акцента, остальные белые, лесенкой вправо.
    lines.forEach((line, i) => {
      const padX = line.size * 0.06, padY = line.size * 0.12;
      rect(line.x - padX, line.y - padY, line.width + 2 * padX, line.height + 2 * padY, i === accented, surface);
    });
  }
  if (design === 'liquid' && lines.length) {
    // Одна белая фигура под всеми строками: прямоугольники строк сливаются, все углы скруглены.
    // Высота строки — по метрикам шрифта, а не по её буквам: строчная строка без хвостов
    // («в первом экране») иначе получает у нижнего края почти нулевой отступ. Как у плашки субтитров:
    // от высоты заглавной и от базовой линии одинаковый отступ, которого хватает на хвосты «р», «у», «д».
    const deep = measure('рдуДЦ', best.size).descent;
    const cap = measure('Н', best.size).height;
    const padX = best.size * 0.48;
    const padY = Math.max(best.size * 0.4, (deep ?? 0) + best.size * 0.2);
    const rows = lines.map((line, i) => {
      const descent = metrics[i]!.descent;
      if (deep === undefined || descent === undefined) {
        return { left: line.x - padX, right: line.x + line.width + padX, top: line.y - padY, bottom: line.y + line.height + padY };
      }
      const baseline = line.y + line.height - descent;
      return { left: line.x - padX, right: line.x + line.width + padX,
        top: Math.min(line.y, baseline - cap * line.size / best.size) - padY, bottom: baseline + padY };
    });
    shapes.push({ path: liquidPath(rows, best.size * 0.34), accent: false, color: surface, opacity: 1, outlineWidth: 0 });
  }
  if (groups.length && design === 'frame') {
    // Open corner brackets frame the title without a continuous border.
    if (box) rect(card.x, card.y, card.width, card.height, false, surface, 0, light ? 0.92 : 0.8);
    const thickness = width * 0.007;
    const arm = Math.min(width * 0.065, card.height * 0.28);
    for (const right of [false, true]) for (const bottom of [false, true]) {
      const x = right ? card.x + card.width - arm : card.x;
      const y = bottom ? card.y + card.height - thickness : card.y;
      rect(x, y, arm, thickness, true);
      rect(right ? card.x + card.width - thickness : card.x,
        bottom ? card.y + card.height - arm : card.y, thickness, arm, true);
    }
  }
  if (accentMode === 'none') {
    // «Без акцента»: всё, что красилось цветом подсветки, становится белым.
    // У «Хайлайта» акцентная плашка становится обычной: цвета плашки, с контрастным текстом.
    for (const shape of shapes) if (shape.accent) { shape.accent = false; shape.color = design === 'highlight' ? surface : '#FFFFFF'; }
    for (const line of lines) { line.accent = false; if (design === 'highlight') line.color = ink; }
  }
  return { lines, shapes, card, lower };
}

/** Расставляет буквы строки по кривой «Волны»: по длине дуги, с поворотом по касательной. */
function waveGlyphs(line: HookLine, measure: HookMeasure): HookGlyph[] {
  const { size } = line;
  const amplitude = size * WAVE.amplitude, period = size * WAVE.period, phase = size * WAVE.phase;
  const baseline = line.y + line.height;
  const curve = (u: number) => amplitude * Math.sin(2 * Math.PI * (u + phase) / period);
  // Таблица длины дуги с шагом в десятую кегля: по ней ищем u для середины каждой буквы.
  const step = size / 10;
  const arc: number[] = [0];
  for (let u = step; u <= line.width * 1.2 + step; u += step) {
    arc.push(arc.at(-1)! + Math.hypot(step, curve(u) - curve(u - step)));
  }
  const at = (s: number) => {
    let i = 1;
    while (i < arc.length - 1 && arc[i]! < s) i++;
    return (i - 1 + (s - arc[i - 1]!) / Math.max(1e-6, arc[i]! - arc[i - 1]!)) * step;
  };
  const glyphs: HookGlyph[] = [];
  const chars = [...line.text];
  let start = 0;
  chars.forEach((char, i) => {
    const end = measure(chars.slice(0, i + 1).join(''), size).width * HOOK_SCALE_X;
    const advance = end - start;
    if (char.trim()) {
      const u = at(start + advance / 2);
      const slope = (curve(u + step / 2) - curve(u - step / 2)) / step;
      const ox = line.x + u, oy = baseline + curve(u);
      // Экранная ось y вниз: подъём кривой — поворот против часовой стрелки.
      glyphs.push({ text: char, x: ox - advance / 2, y: oy - line.height, ox, oy, angle: -Math.atan(slope) * 180 / Math.PI });
    }
    start = end;
  });
  return glyphs;
}

function ribbonPoints(x: number, y: number, w: number, h: number, cut: number): [number, number][] {
  return [[x + cut, y], [x + w, y], [x + w - cut, y + h], [x, y + h]];
}
function polygon(points: [number, number][]): string {
  return points.map(([x, y], i) => `${i ? 'L' : 'M'} ${x.toFixed(2)} ${y.toFixed(2)}`).join(' ') + ' Z';
}

/**
 * Контур «Жидкого хайлайта» (Figma 28254:11543): ступенчатый многоугольник из строк,
 * у которого скруглены все углы — и внешние, и внутренние, радиус не больше половины сторон.
 */
function liquidPath(rows: { left: number; right: number; top: number; bottom: number }[], radius: number): string {
  const points: [number, number][] = [];
  const push = (x: number, y: number) => {
    const last = points.at(-1);
    if (last && Math.abs(last[0] - x) < 0.01 && Math.abs(last[1] - y) < 0.01) return;
    points.push([x, y]);
  };
  // Строки сливаются, но каждая сохраняет свои отступы: выступ строки начинается на её собственном
  // верхнем крае, а не посередине между строками, — над выступом такой же отступ, как снизу.
  const step = (upper: (typeof rows)[number], lower: (typeof rows)[number], lowerSticksOut: boolean) =>
    lower.top >= upper.bottom ? (upper.bottom + lower.top) / 2 : lowerSticksOut ? lower.top : upper.bottom;
  push(rows[0]!.left, rows[0]!.top);
  push(rows[0]!.right, rows[0]!.top);
  for (let i = 0; i + 1 < rows.length; i++) {
    const a = rows[i]!, b = rows[i + 1]!, y = step(a, b, b.right > a.right);
    push(a.right, y); push(b.right, y);
  }
  push(rows.at(-1)!.right, rows.at(-1)!.bottom);
  push(rows.at(-1)!.left, rows.at(-1)!.bottom);
  for (let i = rows.length - 2; i >= 0; i--) {
    const a = rows[i]!, b = rows[i + 1]!, y = step(a, b, b.left < a.left);
    push(b.left, y); push(a.left, y);
  }
  const first = points[0]!, last = points.at(-1)!;
  if (Math.abs(first[0] - last[0]) < 0.01 && Math.abs(first[1] - last[1]) < 0.01) points.pop();
  // Убираем точки на одной прямой: на них нечего скруглять.
  const corners = points.filter((p, i) => {
    const a = points[(i - 1 + points.length) % points.length]!, b = points[(i + 1) % points.length]!;
    return !((Math.abs(a[0] - p[0]) < 0.01 && Math.abs(b[0] - p[0]) < 0.01) || (Math.abs(a[1] - p[1]) < 0.01 && Math.abs(b[1] - p[1]) < 0.01));
  });
  const k = 0.55228475;
  const parts = corners.map((p, i) => {
    const a = corners[(i - 1 + corners.length) % corners.length]!, b = corners[(i + 1) % corners.length]!;
    const la = Math.hypot(a[0] - p[0], a[1] - p[1]), lb = Math.hypot(b[0] - p[0], b[1] - p[1]);
    const r = Math.min(radius, la / 2, lb / 2);
    const from: [number, number] = [p[0] + (a[0] - p[0]) / la * r, p[1] + (a[1] - p[1]) / la * r];
    const to: [number, number] = [p[0] + (b[0] - p[0]) / lb * r, p[1] + (b[1] - p[1]) / lb * r];
    const c1 = [from[0] + (p[0] - from[0]) * k, from[1] + (p[1] - from[1]) * k];
    const c2 = [to[0] + (p[0] - to[0]) * k, to[1] + (p[1] - to[1]) * k];
    return { from, to, c1, c2 };
  });
  const f = (v: number) => v.toFixed(2);
  return parts.map((c, i) => `${i ? 'L' : 'M'} ${f(c.from[0])} ${f(c.from[1])} C ${f(c.c1[0]!)} ${f(c.c1[1]!)} ${f(c.c2[0]!)} ${f(c.c2[1]!)} ${f(c.to[0])} ${f(c.to[1])}`).join(' ') + ' Z';
}

/** Cubic rounded rectangle supported identically by SVG and ASS vector drawings. */
function roundedRect(x: number, y: number, width: number, height: number, radius: number): string {
  const r = Math.min(radius, width / 2, height / 2);
  const c = r * 0.55228475;
  const right = x + width, bottom = y + height;
  return `M ${x + r} ${y} L ${right - r} ${y} C ${right - r + c} ${y} ${right} ${y + r - c} ${right} ${y + r}
    L ${right} ${bottom - r} C ${right} ${bottom - r + c} ${right - r + c} ${bottom} ${right - r} ${bottom}
    L ${x + r} ${bottom} C ${x + r - c} ${bottom} ${x} ${bottom - r + c} ${x} ${bottom - r}
    L ${x} ${y + r} C ${x} ${y + r - c} ${x + r - c} ${y} ${x + r} ${y} Z`
    .replace(/-?\d+(?:\.\d+)?/g, value => Number(value).toFixed(2)).replace(/\s+/g, ' ');
}
